<template>
  <div
    class="comment-editor-parts"
    :class="[
      `comment-editor-parts--${screenOrientationType}`,
      { 'comment-editor-parts--active': !(isCommentEmpty || isSubmitDisabled) },
    ]"
  >
    <AtomTextArea
      class="comment-editor-parts__textarea"
      v-model="messageText"
      :focusOnMount="true"
      :placeholder="$tc('RaceVideoPage.highlights.comment.placeholder')"
    />

    <div class="comment-editor-parts__buttons">
      <div class="comment-editor-parts__count">
        <comment-count-parts :comment="messageText" :commentLength="messageLength" />
      </div>
      <button
        class="comment-editor-parts__button comment-editor-parts__button--cancel"
        @click="emitChancel"
      >
        <highlights-cancel-icon />
      </button>
      <button
        :disabled="isCommentEmpty || isSubmitDisabled"
        class="comment-editor-parts__button comment-editor-parts__button--save"
        @click="emitPost"
      >
        <highlights-submit-icon />
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from '@vue/composition-api'
import twitterText from 'twitter-text'
import AtomTextArea from '@/components/atoms/input/AtomTextArea.vue'
import HighlightsSubmitIcon from '@/components/RaceVideoPage/RaceVideoHighlightsPane/parts/icons/HighlightsSubmitIcon.vue'
import HighlightsCancelIcon from '@/components/RaceVideoPage/RaceVideoHighlightsPane/parts/icons/HighlightsCancelIcon.vue'
import CommentCountParts from '@/components/RaceVideoPage/RaceVideoHighlightsPane/parts/CommentCountParts.vue'
import Const from '@/util/Const'

/**
 * レース動画再生画面 ハイライト 投稿コメント編集 パーツコンポーネント
 */
export default defineComponent({
  name: 'CommentEditorParts',
  components: {
    CommentCountParts,
    HighlightsCancelIcon,
    HighlightsSubmitIcon,
    AtomTextArea,
  },
  props: {
    /**
     * 画面向き
     */
    screenOrientationType: {
      type: String,
      default: 'portrait-primary',
    },
    /**
     * コメントメッセージ
     */
    message: {
      type: String as () => string | null,
      default: '',
    },
  },
  setup() {
    // 編集キャンセル確認用モーダル
    const enabledConfirmModal = ref<boolean>(false)
    const messageText = ref('')

    /**
     * コメントの文字数
     */
    const messageLength = computed(() => twitterText.parseTweet(messageText.value).weightedLength)

    /**
     * コメントが文字数オーバーの場合、サブミットボタンをdisabledにする
     */
    const isSubmitDisabled = computed(
      () => messageLength.value > Const.MAX_HIGHLIGHT_COMMENT_LENGTH,
    )

    /**
     * コメントが空かどうか
     * スペースのみ、改行のみを空と扱う
     */
    const isCommentEmpty = computed(() => !messageText.value.replace(/\s+/g, ''))

    return {
      enabledConfirmModal,
      messageText,
      messageLength,
      isSubmitDisabled,
      isCommentEmpty,
    }
  },
  mounted() {
    this.messageText = JSON.parse(JSON.stringify(this.message))
  },
  methods: {
    emitChancel() {
      this.$emit('handlerCancel')
    },
    emitPost() {
      // 入力状態でコメントがある場合
      if (!this.isCommentEmpty && !this.isSubmitDisabled) {
        this.$emit('handlerSave', this.messageText)
      }
    },
  },
})
</script>

<style lang="scss" scoped>
.comment-editor-parts {
  display: flex;
  flex-flow: column nowrap;
  align-content: flex-end;
  justify-items: flex-start;
  gap: 6px;
  width: 100%;

  &__textarea {
    width: 100%;
    min-height: 6.68em;
    padding: 0;
    font-family: $fontSignup;
    font-weight: 500;
    font-size: 12px;
    line-height: 1.67;
    letter-spacing: 0.02em;
    background: transparent;
    color: $white;
    overflow-wrap: break-word;
    border: none;

    &:focus {
      background: transparent;
    }
  }

  &__buttons {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
  }

  &__count {
    margin-right: 10px;
  }

  &__button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;

    &--cancel {
      width: 24px;
      height: 24px;
      color: rgba(#c2c2c2, 0.9);
    }

    &--save {
      margin-right: -12px;
      opacity: 0.5;
      color: $white;

      svg path {
        stroke: $green-light;
      }
    }
  }

  &--active {
    .comment-editor-parts__button {
      &--save {
        opacity: 1;
        color: $green-light;
      }
    }
  }

  &--landscape-primary,
  &--landscape-secondary {
    .comment-editor-parts {
      &__button {
        &--save {
          margin-right: -10px;
        }
      }
    }
  }
}
</style>
